import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Dialog from '../../../../dialog/Dialog'
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import UploadField from '../../../../commom/FormControl/UploadField'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material'
import ContentFile from './ContentFile'
import LoadingSpinner from '../../../../Loading/LoadingSpinner'
import { readFileCSV } from '../../../../../utils/file'
import MenuIconButton from '../../../../button/MenuIconButton'
import LearningContinuumHelper from '../../../../../helper/import-learning-continuum.helper'

// importModes.js
export const IMPORT_MODES = {
  LC: {
    label: 'Learning Continuum',
    description: 'Create new learning continuums with their levels and definitions.',
    title: 'Import Learning Continuum from file',
    templateUrl: '/templates/lc-import-template.csv',
    cellMeaning: 'definition',
    endpoint: '',
    canCreateElements: true
  },
  TEACHING_SUGGESTIONS: {
    label: 'Teaching Suggestions',
    description: 'Add teaching suggestions to the levels of existing learning continuums.',
    title: 'Import Teaching Suggestions from file',
    templateUrl: '/templates/lc-teaching-suggestions-template.csv',
    cellMeaning: 'teaching suggestion',
    endpoint: 'teaching-suggestion',
    strategy: 'replace',
    canCreateElements: false
  },
  WORKED_EXAMPLES: {
    label: 'Worked Examples',
    description: 'Add worked examples to the levels of existing learning continuums.',
    title: 'Import Worked Examples from file',
    templateUrl: '/templates/lc-worked-examples-template.csv',
    cellMeaning: 'worked examples',
    endpoint: 'worked-examples',
    strategy: 'replace',
    canCreateElements: false
  }
}

const IMPORT_MODES_KEYS = Object.keys(IMPORT_MODES).map((key) => key)

const DialogImport = () => {
  const { curriculumId } = useParams()
  const columnsTable = useSelector((state) => state?.datatable?.lc?.[curriculumId] || [])
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})
  const maxFileSize = 20 * 1024 * 1024 // 20 MB
  const [loading, toggleLoading] = useState(false)
  const [errors, setInputErrors] = useState({})
  const [modal, setModal] = useState(null)
  const [files, setFiles] = useState([])
  const [contentFile, setContentFile] = useState(null)
  const [columnsHeaders, setColumnsHeaders] = useState([])
  const [newElements, setNewElements] = useState([])
  const [loadingFile, toggleLoadingFile] = useState(false)
  const [createMissing, setCreateMissing] = useState(false)
  const [type, setType] = useState('LC')
  const { importLearningContinuum, importLearningContinuumType } = useLearningContinuum()

  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }
  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }
  const onClose = () => {
    setModal(false)
    setFiles(null)
    setContentFile(null)
    setNewElements([])
    setCreateMissing(false)
    setInputErrors({})
  }

  const onDownloadTemplate = () => {
    const csvContent = columnsHeaders
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const fileName = `learning_continuum_template_${currentCurriculum.shortName || 'curriculum'}_${type}.csv`
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onReadFile = async (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No file provided'))
        return
      }
      const reader = new FileReader()
      reader.onload = (event) => resolve(event.target.result)
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file.blob || file)
    })
  }
  const onConfirm = async (e) => {
    e.preventDefault()
    if (!contentFile || !files?.length) {
      setInputErrors({ message: 'Please upload a valid CSV file before importing' })
      return
    }
    toggleLoading(true)
    try {
      let result = { error: false }

      if (!IMPORT_MODES_KEYS.includes(type)) {
        setInputErrors({ message: 'Invalid type' })
        return
      }

      if (type === 'LC') {
        result = await importLearningContinuum(files[0], { createMissing })
      }

      if (['TEACHING_SUGGESTIONS', 'WORKED_EXAMPLES'].includes(type)) {
        result = await importLearningContinuumType(files[0], IMPORT_MODES[type].endpoint)
      }

      if (result.error) {
        setInputErrors(result)
      } else {
        onClose(false)
      }
    } finally {
      toggleLoading(false)
    }
  }

  const onLoadContentFile = async (content, createMissing) => {
    let headers = []
    let parsedHeader = []
    let parsedContent = []
    let isValidHeader = true
    await readFileCSV(content)
      .then((result) => {
        if (result.error) {
          setInputErrors({ message: result.error })
        } else {
          const {
            isValidHeader: isValidH,
            headers: h,
            content
          } = LearningContinuumHelper.parsedHeader(result.data, currentCurriculum)

          headers = result.data[0]
          parsedHeader = h
          isValidHeader = isValidH
          parsedContent = content
        }
      })
      .catch((error) => {
        setInputErrors({ message: error.message || 'Error parsing CSV content' })
      })

    if (headers.length === 0) {
      setInputErrors({ message: 'CSV file must have headers' })
      return
    }
    if (!columnsHeaders.every((col) => col.label === 'version' || headers.includes(col))) {
      const missingHeaders = columnsHeaders.filter((col) => col.label !== 'version' && !headers.includes(col))
      setInputErrors({
        message: `CSV file must include all required headers`,
        messageDetails: `${missingHeaders.map((col) => `- ${col}`).join('\n')}`
      })
      return
    }
    if (!isValidHeader) {
      setInputErrors({
        message: `CSV file Invalid header, some required fields are missing`
      })
      return
    }
    if (parsedContent.length === 0) {
      setInputErrors({ message: 'CSV file must have at least one data row' })
      return
    }

    const parsedContentValid = LearningContinuumHelper.parsedContent(parsedHeader, parsedContent, {
      createMissing
    })
    const newElements = LearningContinuumHelper.collectNewElements(parsedContentValid)
    setNewElements(newElements)
    setContentFile(parsedContentValid)
    return
  }

  useEffect(() => {
    if (files?.length > 0) {
      toggleLoadingFile(true)
      setInputErrors({})
      setContentFile(null)
      onReadFile(files[0])
        .then((content) => onLoadContentFile(content, createMissing))
        .catch((error) => {
          setInputErrors({ message: error.message || 'Error reading file' })
        })
        .finally(() => {
          toggleLoadingFile(false)
        })
    } else {
      setContentFile(null)
      setInputErrors({})
    }
  }, [files, createMissing])

  useEffect(() => {
    const columns = columnsTable
      ?.filter((column) => column.key !== 'version')
      .map((column) => `${column.type}_${column.label}`)
    const { levels } = currentCurriculum
    levels
      ?.sort((a, b) => a.position - b.position)
      ?.forEach((element) => {
        columns.push(`levels_${element.name}`)
      })
    setColumnsHeaders(columns)
  }, [columnsTable, currentCurriculum])

  const Actions = (
    <div className='w-full flex gap-4 justify-between'>
      <div className='flex gap-4 justify-end items-center'>
        <ButtonStartIcon
          onClick={onDownloadTemplate}
          color='secondary'
          // variant='outlined'
          disabled={loading}
          startIcon={'download'}
        >
          template
        </ButtonStartIcon>
      </div>
      <div className='flex gap-4 justify-end'>
        <ButtonStartIcon
          onClick={onClose}
          color='primary'
          variant='outlined'
          disabled={loading}
          startIcon={['fal', 'times']}
        >
          Cancel
        </ButtonStartIcon>
        <ButtonStartIcon
          onClick={onConfirm}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          confirm
        </ButtonStartIcon>
      </div>
    </div>
  )
  return (
    <>
      <MenuIconButton
        ref={anchorRef}
        disabled={loading}
        title='Import Learning Continuum'
        icon='file-import'
        startIcon={'file-import'}
        style={{
          borderRadius: '0.5rem',
          backgroundColor: '#00218c',
          color: '#e6f4ea',
          padding: '0.45rem',
          opacity: loading ? 0.5 : 1
        }}
        items={Object.keys(IMPORT_MODES).map((key) => ({
          _id: key,
          permissions: true,
          label: IMPORT_MODES[key].label,
          onClick: () => {
            handleClose()
            setModal(true)
            setType(key)
          }
        }))}
      />
      {modal && (
        <Dialog
          id={`learning-continuum-import`}
          show={modal}
          onClose={onClose}
          onConfirm={onConfirm}
          loading={loading}
          title={IMPORT_MODES[type]?.title}
          onCloseHeader={false}
          maxWidth={'sm'}
          actions={Actions}
        >
          <Box className='flex flex-col gap-2'>
            {loadingFile && <LoadingSpinner />}
            <Typography color='primary' variant='h5'>
              {IMPORT_MODES[type]?.label}
            </Typography>
            <Typography className='text-muted-foreground opacity-75'>
              {IMPORT_MODES[type]?.description}
            </Typography>
            <UploadField
              files={files}
              setFiles={setFiles}
              placeholder='Upload CSV file'
              isLoading={loading}
              maxFileSize={maxFileSize}
              multiple={false}
              formatAcceptedObj={{
                'text/csv': ['.csv']
              }}
            />
            <Box>
              {IMPORT_MODES[type].canCreateElements && (
                <FormControlLabel
                  control={
                    <Checkbox checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />
                  }
                  label={
                    <Typography>
                      Create missing curriculum elements. <small>The learning area must already exist.</small>
                    </Typography>
                  }
                />
              )}
              {newElements.length > 0 && (
                <Box className='pl-8'>
                  <summary>
                    {newElements.length} new curriculum element
                    {newElements.length > 1 ? 's' : ''} will be created
                  </summary>
                  <TableContainer className='h-20'>
                    <Table>
                      <TableBody>
                        {newElements.map((el, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <strong>{el.name}</strong> · {el.level}
                              <span> under {el.parentName}</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
            {errors?.message && (
              <Alert severity='error'>
                {errors?.message}
                {errors?.messageDetails && <pre>{errors?.messageDetails}</pre>}
              </Alert>
            )}
            {contentFile && (
              <ContentFile
                contentFile={contentFile || []}
                columnsHeaders={columnsHeaders || []}
                loading={loading || loadingFile}
              />
            )}
          </Box>
        </Dialog>
      )}
    </>
  )
}

export default DialogImport
